/**
 * Workbook metadata for “which filters produced this sheet” — stored per worksheet id
 * (see {@link workbookNamedItemKeyForSheetId}).
 */
export const TRACE_SHEET_FILTER_PAYLOAD_VERSION = 1 as const;

export type TraceSheetFilterFieldValue = string | string[];

export type TraceSheetFilterPersistedV1 = {
    readonly version: typeof TRACE_SHEET_FILTER_PAYLOAD_VERSION;
    /** Reporting target id (`#report-target` value). */
    readonly targetId: string;
    /** `#filter-record-type` value when the target defines a record type row; otherwise `null`. */
    readonly recordType: string | null;
    /** Values keyed by filter field `elementId` (multiselect = string[]). */
    readonly fieldValues: Readonly<Record<string, TraceSheetFilterFieldValue>>;
    /** Same fingerprint as {@link buildTraceFetchFingerprintString} at save time; restores Refresh eligibility. */
    readonly fetchFingerprint: string | null;
    /** ISO timestamp when the snapshot was written. */
    readonly savedAt: string;
};

export function isTraceSheetFilterPersistedV1(
    value: unknown
): value is TraceSheetFilterPersistedV1 {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const o = value as Record<string, unknown>;
    if (o.version !== TRACE_SHEET_FILTER_PAYLOAD_VERSION) {
        return false;
    }
    if (typeof o.targetId !== 'string' || o.targetId.trim() === '') {
        return false;
    }
    if (o.recordType !== null && typeof o.recordType !== 'string') {
        return false;
    }
    if (!o.fieldValues || typeof o.fieldValues !== 'object' || Array.isArray(o.fieldValues)) {
        return false;
    }
    if (
        o.fetchFingerprint !== undefined &&
        o.fetchFingerprint !== null &&
        typeof o.fetchFingerprint !== 'string'
    ) {
        return false;
    }
    if (typeof o.savedAt !== 'string') {
        return false;
    }
    return true;
}

/**
 * Lenient parse for JSON decoded from workbook storage.
 * Returns `null` when shape is invalid or version unsupported.
 */
export function parseTraceSheetFilterPersistedJson(
    text: string
): TraceSheetFilterPersistedV1 | null {
    let parsed: unknown;
    try {
        parsed = JSON.parse(text) as unknown;
    } catch {
        return null;
    }
    if (!isTraceSheetFilterPersistedV1(parsed)) {
        return null;
    }
    const fv = parsed.fieldValues as Record<string, unknown>;
    const normalizedEntries: [string, TraceSheetFilterFieldValue][] = [];
    for (const [k, v] of Object.entries(fv)) {
        if (typeof k !== 'string' || k.trim() === '') {
            continue;
        }
        if (typeof v === 'string') {
            normalizedEntries.push([k, v]);
        } else if (Array.isArray(v) && v.every((item) => typeof item === 'string')) {
            normalizedEntries.push([k, v as string[]]);
        } else {
            return null;
        }
    }
    return {
        version: TRACE_SHEET_FILTER_PAYLOAD_VERSION,
        targetId: parsed.targetId.trim(),
        recordType: parsed.recordType,
        fieldValues: Object.fromEntries(normalizedEntries),
        fetchFingerprint:
            parsed.fetchFingerprint === undefined
                ? null
                : typeof parsed.fetchFingerprint === 'string'
                  ? parsed.fetchFingerprint
                  : null,
        savedAt: parsed.savedAt,
    };
}
